import type { CraftBlock, Meeting, SpeakerSegment } from '../../types';
import { toFileUrl } from '../../lib/fileUrl';
import { dateLocale, t } from '../../lib/i18n';
import { displayFields } from '../meetings/mapMeeting';
import {
  isGenericMeetingTitle,
  MIN_SUMMARY_SECONDS,
  noSpeechMessage,
  shortRecordingMessage,
  summaryFailedMessage,
} from './summaryRules';
import { displaySpeaker } from './speakerLabels';

export interface RecordingDraftInput {
  transcript: SpeakerSegment[];
  durationSeconds: number;
  /** Kayıt klasöründeki ses dosyası (ana süreç yazdı). */
  filePath: string;
  /** Kullanıcının verdiği başlık; boş ya da varsayılansa başlığı yapay zekâ verir. */
  userTitle: string;
  /** Kısa kayıtta ve yapay zekâ başlık vermezse kullanılacak başlık. */
  fallbackTitle: string;
  recordedAt: Date;
  space: Meeting['space'];
  participants: Meeting['participants'];
  tags: string[];
  /** Notun başındaki bilgi kutusu — konuşmacılar ve transkript var mı biliniyor. */
  callout: (info: { speakers: string[]; hasTranscript: boolean }) => string;
  /** Bilgi kutusundan sonra gelen bloklar (ör. kayıt sırasında alınan notlar). */
  extraBlocks?: CraftBlock[];
  /** Özet istenmeden hemen önce çağrılır (arayüz "Özetleniyor…" göstersin). */
  onSummarizing?: () => void;
}

/**
 * Transkriptten kaydedilmeye hazır bir toplantı taslağı: transkript varsa ve
 * kayıt yeterince uzunsa yapay zekâ özeti istenir. Özet başarısız olursa kayıt
 * yine saklanır — özet üretilemedi diye kullanıcının kaydını kaybetmek olmaz.
 * Kayıt sayfası ve ses dosyası yükleme aynı taslağı üretir.
 */
export async function buildRecordingDraft(input: RecordingDraftInput): Promise<Omit<Meeting, 'id'>> {
  const userTitle = input.userTitle.trim();
  const hasCustomTitle = Boolean(userTitle) && !isGenericMeetingTitle(userTitle);
  const hasTranscript = input.transcript.length > 0;
  // 1 dakikadan kısa kayıtlar özetletilmez — ses ve transkript yine saklanır.
  const isTooShort = input.durationSeconds < MIN_SUMMARY_SECONDS;
  const speakers = Array.from(new Set(input.transcript.map((s) => displaySpeaker(s.speaker))));

  let finalTitle = hasCustomTitle ? userTitle : input.fallbackTitle;
  let summary: Meeting['summary'] = {
    tldr: isTooShort ? shortRecordingMessage() : hasTranscript ? summaryFailedMessage() : noSpeechMessage(),
    keyPoints: [],
    decisions: [],
  };
  let actionItems: Meeting['actionItems'] = [];

  if (hasTranscript && !isTooShort) {
    input.onSummarizing?.();
    try {
      const generated = await window.zabit.summarizeMeeting(input.transcript, hasCustomTitle ? userTitle : '');
      summary = {
        tldr: generated.tldr || summary.tldr,
        keyPoints: generated.keyPoints,
        decisions: generated.decisions,
      };
      actionItems = generated.actionItems.map((item, index) => ({
        id: `act-${Date.now()}-${index}`,
        text: item.text,
        assignee: item.assignee || t('Ben', 'Me'),
        completed: false,
      }));
      if (!hasCustomTitle && generated.title) finalTitle = generated.title;
    } catch {
      // Özet başarısız — yukarıdaki varsayılan metinle devam.
    }
  }

  const seed = Date.now();
  return {
    title: finalTitle,
    recordedAtISO: input.recordedAt.toISOString(),
    ...displayFields(input.recordedAt.toISOString(), input.durationSeconds),
    status: 'recorded',
    space: input.space,
    participants: input.participants,
    tags: input.tags,
    summary,
    actionItems,
    craftBlocks: [
      { id: `cb-${seed}-1`, type: 'heading', content: finalTitle },
      { id: `cb-${seed}-2`, type: 'callout', content: input.callout({ speakers, hasTranscript }) },
      ...(input.extraBlocks ?? []),
      ...summary.decisions.map((decision, index) => ({
        id: `cb-${seed}-d${index}`,
        type: 'decision' as const,
        content: decision,
      })),
    ],
    transcript: input.transcript,
    audioUrl: toFileUrl(input.filePath),
    audioFilePath: input.filePath,
    // Kayıt önce yerelde; ana süreç arka planda S3'e taşıyınca liste tazelenir.
    storage: 'local',
    audioDurationSeconds: input.durationSeconds,
  };
}

/** "14:05" — kısa kayıtların başlığı için. */
export function clockLabel(date: Date): string {
  return date.toLocaleTimeString(dateLocale(), { hour: '2-digit', minute: '2-digit' });
}
